<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :style="style"
    aria-hidden="true"
  >
    <!-- concrete-beam: tall portrait rectangle — beam cross-section, height > width -->
    <template v-if="name === 'concrete-beam'">
      <rect x="7" y="1.5" width="10" height="21" rx="0.5" />
      <rect x="9" y="3.5" width="6" height="17" rx="0.5" opacity="0.55" />
      <!-- tension rebars bottom -->
      <circle cx="8.5" cy="20.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="20.5" r="1" fill="currentColor" stroke="none" />
      <!-- compression rebars top -->
      <circle cx="8.5" cy="3" r="0.85" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="3" r="0.85" fill="currentColor" stroke="none" />
    </template>

    <!-- rc-column: square block — column cross-section with tie + 8 rebars -->
    <template v-else-if="name === 'rc-column'">
      <rect x="3.5" y="3.5" width="17" height="17" rx="0.5" />
      <rect x="6" y="6" width="12" height="12" rx="0.5" opacity="0.55" />
      <!-- corner rebars -->
      <circle cx="5" cy="5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="5" cy="19" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="19" r="1.1" fill="currentColor" stroke="none" />
      <!-- mid-side rebars -->
      <circle cx="12" cy="5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="5" cy="12" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="0.9" fill="currentColor" stroke="none" />
    </template>

    <!-- slab: Slab plan view with bidirectional reinforcement mesh -->
    <template v-else-if="name === 'slab'">
      <rect x="3" y="3" width="18" height="18" rx="0.5" />
      <line x1="3" y1="8" x2="21" y2="8" opacity="0.7" />
      <line x1="3" y1="12" x2="21" y2="12" opacity="0.7" />
      <line x1="3" y1="16" x2="21" y2="16" opacity="0.7" />
      <line x1="8" y1="3" x2="8" y2="21" opacity="0.7" />
      <line x1="12" y1="3" x2="12" y2="21" opacity="0.7" />
      <line x1="16" y1="3" x2="16" y2="21" opacity="0.7" />
    </template>

    <!-- footing: Pad footing elevation — column stub + trapezoidal pad + bottom rebars -->
    <template v-else-if="name === 'footing'">
      <line x1="2" y1="14" x2="6" y2="14" />
      <line x1="18" y1="14" x2="22" y2="14" />
      <rect x="9.5" y="4" width="5" height="10" />
      <path d="M3.5 14 L20.5 14 L18.5 20 L5.5 20 Z" />
      <line x1="6" y1="18" x2="18" y2="18" opacity="0.6" />
      <circle cx="8" cy="18" r="0.7" fill="currentColor" stroke="none" />
      <circle cx="12" cy="18" r="0.7" fill="currentColor" stroke="none" />
      <circle cx="16" cy="18" r="0.7" fill="currentColor" stroke="none" />
    </template>

    <!-- retaining-wall: L-cantilever wall + soil hatching -->
    <template v-else-if="name === 'retaining-wall'">
      <path d="M8 3 L11 3 L11 18 L20 18 L20 21 L4 21 L4 18 L8 18 Z" />
      <line x1="3" y1="6" x2="6.5" y2="9.5" opacity="0.5" />
      <line x1="3" y1="10" x2="6.5" y2="13.5" opacity="0.5" />
      <line x1="3" y1="14" x2="6.5" y2="17.5" opacity="0.5" />
      <line x1="3" y1="5" x2="8" y2="5" opacity="0.6" />
    </template>

    <!-- crack-width: Beam with jagged crack + caliper marks -->
    <template v-else-if="name === 'crack-width'">
      <rect x="3" y="7" width="18" height="10" rx="0.5" />
      <path d="M12 17 L11 14 L13 12 L11.5 9.5 L12.5 7" stroke-width="1.4" />
      <path d="M9 19.5 L9 20.5 L15 20.5 L15 19.5" opacity="0.7" stroke-width="1.3" />
    </template>

    <!-- steel-column: Wide-flange (I/H) cross-section -->
    <template v-else-if="name === 'steel-column'">
      <line x1="4" y1="5" x2="20" y2="5" stroke-width="2" />
      <line x1="4" y1="19" x2="20" y2="19" stroke-width="2" />
      <line x1="12" y1="5" x2="12" y2="19" stroke-width="2" />
      <line x1="4" y1="7" x2="20" y2="7" opacity="0.45" />
      <line x1="4" y1="17" x2="20" y2="17" opacity="0.45" />
    </template>

    <!-- base-plate: Base plate plan — column footprint + 4 anchor bolts -->
    <template v-else-if="name === 'base-plate'">
      <rect x="3" y="3" width="18" height="18" rx="0.5" />
      <line x1="8" y1="9" x2="16" y2="9" opacity="0.6" />
      <line x1="8" y1="15" x2="16" y2="15" opacity="0.6" />
      <line x1="12" y1="9" x2="12" y2="15" opacity="0.6" />
      <circle cx="6" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="18" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="6" cy="18" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="18" cy="18" r="1.2" fill="currentColor" stroke="none" />
    </template>

    <!-- purlin: Sloped rafter with three Z-purlins -->
    <template v-else-if="name === 'purlin'">
      <line x1="3" y1="18" x2="21" y2="6" stroke-width="1.7" />
      <path d="M5.5 16 L8 15 L7 13 L9.5 12" stroke-width="1.5" />
      <path d="M10 13 L12.5 12 L11.5 10 L14 9" stroke-width="1.5" />
      <path d="M14.5 10 L17 9 L16 7 L18.5 6" stroke-width="1.5" />
    </template>

    <!-- gantry: Crane wheel on rail above I-beam girder -->
    <template v-else-if="name === 'gantry'">
      <circle cx="12" cy="6" r="2.2" />
      <line x1="3" y1="9" x2="21" y2="9" stroke-width="1.4" />
      <line x1="3" y1="11" x2="21" y2="11" stroke-width="1.8" />
      <line x1="12" y1="11" x2="12" y2="19" stroke-width="1.8" />
      <line x1="3" y1="19" x2="21" y2="19" stroke-width="1.8" />
    </template>

    <!-- fallback -->
    <template v-else>
      <rect x="4" y="4" width="16" height="16" rx="2" opacity="0.3" />
    </template>
  </svg>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
  name: string
  size?: number
  strokeWidth?: number
  style?: Record<string, string>
}>(), {
  size: 20,
  strokeWidth: 1.6,
  style: () => ({}),
})
</script>
